"use client";

import React, { createContext, useContext, useState } from "react";
import { useSession } from "./SessionContext";

export type SelectedPeopleType = {
  id: number | null;
  firstName: string;
  lastName1: string;
  lastName2: string;
} | null;

export type SelectionType = {
  people: SelectedPeopleType;
  mode: "single" | "multiple";
};

export type AgeType = {
  age_min: string | null;
  age_max: string | null;
};

export type FiltersType = {
  age_min: number | null;
  age_max: number | null;
  country_id: number[];
  city: string;
  category_id: number[] | null;
  state_id: number[];
  interview_status_id: number[];
  tag_id: string[];
  po_box: number[];
  gender_id: number[];
  has_transport: boolean | null;
  has_car_license: boolean | null;
  living_at_home: boolean | null;
  housekeeping: boolean | null;
  worked_previously: boolean | null;
  // unvailable: boolean | null;
  not_hireable: boolean | null;
  not_available: boolean | null;
  only_active: boolean | null;
  unreviewed: boolean | null;
  weight: number | null;
  update_date: string | null;
  created_date: string | null;
  expected_salary: number | null;
  keyword: string;
  grade: number | null;
};

// Define the shape of your session object
export type peopleType = {
  noCountryDialogOpen: boolean;
  dataLoading: boolean;
  filterChanged: number;
  isFilterApplied: boolean;
  isSidebarOpen: boolean;
  filterCount: number;
  filters: FiltersType;
  selection: SelectionType;
};

export const peopleInitialState: peopleType = {
  noCountryDialogOpen: false,
  dataLoading: false,
  filterChanged: 0,
  isFilterApplied: false,
  isSidebarOpen: false,
  selection: {
    people: { id: 0, firstName: "", lastName1: "", lastName2: "" },
    mode: "single",
  },
  filterCount: 0,
  filters: {
    age_min: null,
    age_max: null,
    country_id: [],
    city: "",
    category_id: [],
    state_id: [],
    interview_status_id: [],
    tag_id: [],
    po_box: [],
    gender_id: [],
    has_transport: null,
    has_car_license: null,
    living_at_home: null,
    housekeeping: null,
    worked_previously: null,
    not_hireable: null,
    not_available: null,
    only_active: null,
    unreviewed: null,
    weight: null,
    update_date: null,
    created_date: null,
    expected_salary: null,
    grade: null,
    keyword: "",
  },
};

// Update the type definition in your context
interface peopleContextType {
  people: peopleType;
  setPeople: React.Dispatch<React.SetStateAction<peopleType>>; // This allows both direct state and function updates
  clearFilters: () => void;
  applyFilters: (filters: FiltersType) => void;
}

const peopleContext = createContext<peopleContextType | undefined>(undefined);

export const PeopleProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const { session } = useSession(); // Access the session context
  const { defaultCountryId } = session; // Get the default country ID

  // Use a safe default to prevent hook order issues when session is still loading
  // This ensures the state is always initialized consistently
  const safeDefaultCountryId = defaultCountryId?.id || 53; // Fallback to Costa Rica (53)

  const [people, setPeople] = useState<peopleType>({
    ...peopleInitialState,
    filters: {
      ...peopleInitialState.filters,
      country_id: [safeDefaultCountryId], // Set the default country ID here
    },
  });

  // Function to clear filters
  const clearFilters = () => {
    const safeDefaultCountryId = defaultCountryId?.id || 53; // Fallback to Costa Rica (53)
    setPeople((prev) => ({
      ...prev,
      filterCount: 0,
      isFilterApplied: false,
      isSidebarOpen: false,
      filterChanged: prev.filterChanged + 1,
      filters: {
        ...peopleInitialState.filters,
        country_id: [safeDefaultCountryId], // Set the default country ID here
      },
    }));
  };

  const applyFilters = (filters: FiltersType) => {
    let count = 0;

    // Get default filters for comparison
    const safeDefaultCountryId = defaultCountryId?.id || 53; // Fallback to Costa Rica (53)
    const defaultFilters = {
      ...peopleInitialState.filters,
      country_id: [safeDefaultCountryId], // Set the default country ID here
    };

    for (const key in filters) {
      if (filters.hasOwnProperty(key)) {
        const value = filters[key as keyof FiltersType];
        const defaultValue = defaultFilters[key as keyof FiltersType];

        if (Array.isArray(value) && Array.isArray(defaultValue)) {
          const defaultArray = defaultValue as Array<string | number>;
          count += value.filter((v) => !defaultArray.includes(v)).length;
        } else {
          // Handle boolean filters specially
          if (typeof value === "boolean") {
            // Only count as active filter if value is true (explicitly checked)
            if (value === true) {
              count++;
            }
          } else {
            // For non-boolean filters, check if they differ from default
            if (value !== null && value !== "" && value !== defaultValue) {
              count++;
            }
          }
        }
      }
    }

    // Use filters directly - only interview_status_id: [-1] is used for unreviewed filter
    const cleanedFilters = { ...filters };

    setPeople((prev) => ({
      ...prev,
      isFilterApplied: count > 0,
      isSidebarOpen: false,
      filterChanged: prev.filterChanged + 1,
      filters: cleanedFilters,
    }));
  };

  return (
    <peopleContext.Provider
      value={{
        people,
        setPeople,
        clearFilters,
        applyFilters,
      }}
    >
      {children}
    </peopleContext.Provider>
  );
};

export const usePeople = () => {
  const context = useContext(peopleContext);
  if (context === undefined) {
    throw new Error("usePeople must be used within a Provider");
  }
  return context;
};
